import React, { createElement, Fragment, useMemo, useState, useEffect, useRef, useCallback } from "react";
import rehypeHighlight from "rehype-highlight";
import rehypeReact from "rehype-react";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";
import { MarkdownViewerWrapper } from "./styles";
import { notification } from "antd";
import { useAppSelector, useDarkMode, useThemeColors, ThemeColors } from "redux/hooks";
import mermaid from "mermaid";

/**
 * Given the full markdown text and a current reveal position, checks if that
 * position lands inside a structured block (fenced code block or table).
 * If so, returns the index just past the end of that block so the animation
 * can fast-forward and reveal the block all at once instead of word-by-word.
 * Returns the position unchanged if we're not inside a structured block.
 */
function getBlockEndPosition(fullText: string, position: number): number {
    const revealed = fullText.substring(0, position);

    // Check for unclosed fenced code block (``` or ~~~)
    const fencePattern = /^(`{3,}|~{3,})/gm;
    let match: RegExpExecArray | null;
    let fenceStack: { marker: string; index: number } | null = null;

    // Reset lastIndex
    fencePattern.lastIndex = 0;
    while ((match = fencePattern.exec(revealed)) !== null) {
        const marker = match[1][0]; // '`' or '~'
        const len = match[1].length;
        if (fenceStack === null) {
            fenceStack = { marker, index: match.index };
        } else if (marker === fenceStack.marker && len >= fenceStack.marker.length) {
            // Closing fence
            fenceStack = null;
        }
    }

    if (fenceStack !== null) {
        // We're inside an unclosed code fence — find the closing fence in the full text
        const openEnd = fullText.indexOf('\n', fenceStack.index);
        if (openEnd !== -1) {
            const searchFrom = openEnd + 1;
            const rest = fullText.substring(searchFrom);
            const closeMatch = rest.match(new RegExp(`^(\`{3,}|~{3,})\\s*$`, 'm'));
            if (closeMatch && closeMatch.index !== undefined) {
                const closeEnd = searchFrom + closeMatch.index + closeMatch[0].length;
                return closeEnd;
            } else {
                // No closing fence found yet — don't reveal the partial block.
                // Snap back to just before the opening fence.
                return fenceStack.index;
            }
        }
    }

    // Check for unclosed table: if the current position is in a line starting with |
    // and the table continues further, fast-forward to the end of the table block
    const lastNewline = revealed.lastIndexOf('\n');
    const currentLine = revealed.substring(lastNewline + 1);
    if (currentLine.trimStart().startsWith('|')) {
        // We're in a table row — find the end of the table block
        const lines = fullText.split('\n');
        let charCount = 0;
        let tableEnd = position;
        let inTable = false;
        for (let i = 0; i < lines.length; i++) {
            const lineStart = charCount;
            charCount += lines[i].length + 1; // +1 for \n
            if (lineStart <= position && position < charCount) {
                inTable = true;
            }
            if (inTable) {
                if (!lines[i].trimStart().startsWith('|') && lines[i].trim() !== '') {
                    tableEnd = lineStart;
                    break;
                }
                if (!lines[i].trimStart().startsWith('|') && lines[i].trim() === '') {
                    tableEnd = charCount;
                    break;
                }
                tableEnd = charCount;
            }
        }
        if (tableEnd > position) {
            return Math.min(tableEnd, fullText.length);
        }
    }

    return position;
}

// Global auto-increment counters for unique IDs across all MarkdownViewer instances
let globalCodeBlockId = 0;
let globalMermaidId = 0;

export function MarkdownViewer(props: { markdown: string, greyText?: boolean, resetKey?: string | number, animateReveal?: boolean, onAnimationStateChange?: (isAnimating: boolean) => void }) {
    const { markdown, greyText, resetKey, animateReveal, onAnimationStateChange } = props;
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const expandCodeblocks = useAppSelector(state => state.user.settings?.expand_codeblocks ?? true);

    // Word-by-word reveal animation state
    // Initialize revealedLength based on whether we're animating
    const [revealedLength, setRevealedLength] = useState<number>(() => {
        if (markdown && !animateReveal) {
            return markdown.length;
        }
        return 0;
    });
    const wordsRef = useRef<string[]>([]);
    const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Split markdown into words when it changes
    useEffect(() => {
        if (markdown) {
            // Split into words while preserving spaces and structure
            // Use a regex that captures words and whitespace separately
            wordsRef.current = markdown.match(/\S+|\s+/g) || [];
            if (animateReveal) {
                setRevealedLength(0);
            } else {
                setRevealedLength(markdown.length);
            }
        } else {
            wordsRef.current = [];
            setRevealedLength(0);
        }
    }, [markdown, animateReveal]);

    // Reset animation when resetKey changes
    useEffect(() => {
        if (resetKey !== undefined) {
            if (animateReveal && markdown) {
                setRevealedLength(0);
            }
        }
    }, [resetKey, animateReveal, markdown]);

    // Word-by-word reveal animation
    const currentWordIndexRef = useRef<number>(0);
    const currentTextRef = useRef<string>("");
    const isAnimatingRef = useRef<boolean>(false);
    const onAnimationStateChangeRef = useRef(onAnimationStateChange);

    // Keep the callback ref up to date
    useEffect(() => {
        onAnimationStateChangeRef.current = onAnimationStateChange;
    }, [onAnimationStateChange]);

    useEffect(() => {
        if (!animateReveal || !markdown) {
            // Clear any existing interval
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
                animationIntervalRef.current = null;
            }
            // If animation was running, notify that it stopped
            if (isAnimatingRef.current) {
                isAnimatingRef.current = false;
                onAnimationStateChangeRef.current?.(false);
            }
            // If not animating, show full content
            if (!animateReveal && markdown) {
                setRevealedLength(markdown.length);
            }
            return;
        }

        // Ensure words are split before starting animation
        if (wordsRef.current.length === 0) {
            wordsRef.current = markdown.match(/\S+|\s+/g) || [];
        }

        // If still no words, show full content
        if (wordsRef.current.length === 0) {
            setRevealedLength(markdown.length);
            // Notify that animation is not running (no words to animate)
            if (isAnimatingRef.current) {
                isAnimatingRef.current = false;
                onAnimationStateChangeRef.current?.(false);
            }
            return;
        }

        // Start animation from beginning
        currentWordIndexRef.current = 0;
        currentTextRef.current = "";

        // Notify that animation is starting
        if (!isAnimatingRef.current) {
            isAnimatingRef.current = true;
            onAnimationStateChangeRef.current?.(true);
        }

        // Show first word immediately to ensure something is visible
        if (wordsRef.current.length > 0) {
            currentTextRef.current = wordsRef.current[0];
            currentWordIndexRef.current = 1;
            setRevealedLength(currentTextRef.current.length);
        } else {
            setRevealedLength(0);
        }

        animationIntervalRef.current = setInterval(() => {
            if (currentWordIndexRef.current < wordsRef.current.length) {
                // Add next word
                currentTextRef.current += wordsRef.current[currentWordIndexRef.current];
                currentWordIndexRef.current++;

                // Check if we've entered a structured block (code fence, table).
                // If so, fast-forward to reveal the entire block at once.
                const blockEnd = getBlockEndPosition(markdown, currentTextRef.current.length);
                if (blockEnd > currentTextRef.current.length) {
                    // Fast-forward: advance word index past the block
                    const targetText = markdown.substring(0, blockEnd);
                    currentTextRef.current = targetText;
                    // Advance word index to match
                    let accumulated = '';
                    for (let i = 0; i < wordsRef.current.length; i++) {
                        accumulated += wordsRef.current[i];
                        if (accumulated.length >= targetText.length) {
                            currentWordIndexRef.current = i + 1;
                            break;
                        }
                    }
                } else if (blockEnd < currentTextRef.current.length) {
                    // We're inside an unclosed block with no closing fence yet —
                    // snap back to before the block so we don't render partial content
                    currentTextRef.current = markdown.substring(0, blockEnd);
                }

                setRevealedLength(currentTextRef.current.length);
            } else {
                // Animation complete
                if (animationIntervalRef.current) {
                    clearInterval(animationIntervalRef.current);
                    animationIntervalRef.current = null;
                }
                // Notify that animation completed
                if (isAnimatingRef.current) {
                    isAnimatingRef.current = false;
                    onAnimationStateChangeRef.current?.(false);
                }
            }
        }, 10); // 10ms per word for fast animation

        // Cleanup
        return () => {
            if (animationIntervalRef.current) {
                clearInterval(animationIntervalRef.current);
                animationIntervalRef.current = null;
            }
            // Notify that animation stopped (cleanup)
            if (isAnimatingRef.current) {
                isAnimatingRef.current = false;
                onAnimationStateChangeRef.current?.(false);
            }
        };
    }, [animateReveal, markdown]);

    // Dynamically load highlight.js theme based on dark mode
    useEffect(() => {
        const linkId = 'highlight-theme';
        let link = document.getElementById(linkId) as HTMLLinkElement;

        if (!link) {
            link = document.createElement('link');
            link.id = linkId;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }

        if (darkMode) {
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/atom-one-dark.min.css';
        } else {
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/atom-one-light.min.css';
        }
    }, [darkMode]);

    // Get the markdown to display (either full or partial for animation)
    const displayMarkdown = useMemo(() => {
        if (!markdown) {
            return '';
        }
        if (!animateReveal) {
            return markdown;
        }
        if (revealedLength < markdown.length) {
            return markdown.substring(0, revealedLength);
        }
        return markdown;
    }, [markdown, revealedLength, animateReveal]);

    const content = useMemo(() => {
      try {
        return (
          unified()
            // Parse the raw string
            .use(remarkParse)
            // Add support for GitHub-flavored Markdown
            .use(remarkGfm)
            // Convert the remark tree (Markdown) into a rehype tree (HTML)
            .use(remarkRehype)
            // Add support for syntax highlighting (supports Python, R, Bash, SQL, and many more)
            .use(rehypeHighlight as any, { ignoreMissing: true })
            // Convert the rehype tree (HTML) into a React component tree,
            // with custom components for each element...
            .use(rehypeReact, {
                passNode: true,
                Fragment,
                createElement,
              components: {
                a: ({ href, children }: JSX.IntrinsicElements["a"]) => (
                  <a href={href} target="_blank" rel="noreferrer">{children}</a>
                ),
                h1: ({ children, id }: JSX.IntrinsicElements["h1"]) => (
                  <h1 id={id}>{children}</h1>
                ),
                h2: ({ children, id }: JSX.IntrinsicElements["h2"]) => (
                  <h2 id={id}>{children}</h2>
                ),
                h3: ({ children, id }: JSX.IntrinsicElements["h3"]) => (
                  <h3 id={id}>{children}</h3>
                ),
                h4: ({ children, id }: JSX.IntrinsicElements["h4"]) => (
                  <h4 id={id}>{children}</h4>
                ),
                h5: ({ children, id }: JSX.IntrinsicElements["h5"]) => (
                  <h5 id={id}>{children}</h5>
                ),
                h6: ({ children, id }: JSX.IntrinsicElements["h6"]) => (
                  <h6 id={id}>{children}</h6>
                ),
                p: ({ children }: JSX.IntrinsicElements["p"]) => (
                  <p>{children}</p>
                ),
                strong: ({ children }: JSX.IntrinsicElements["strong"]) => (
                  <strong>{children}</strong>
                ),
                em: ({ children }: JSX.IntrinsicElements["em"]) => (
                  <em>{children}</em>
                ),
                code: (componentProps: JSX.IntrinsicElements["code"]) => (
                  <CodeBlock {...componentProps} greyText={greyText} darkMode={darkMode} expandCodeblocks={expandCodeblocks} />
                ),
                pre: ({ children }: JSX.IntrinsicElements["pre"]) => (
                  <div><pre>{children}</pre></div>
                ),
                ul: ({ children }: JSX.IntrinsicElements["ul"]) => (
                  <ul>{children}</ul>
                ),
                ol: ({ children }: JSX.IntrinsicElements["ol"]) => (
                  <ol>{children}</ol>
                ),
                li: ({ children }: JSX.IntrinsicElements["li"]) => (
                  <li>{children}</li>
                ),
                table: ({ children }: JSX.IntrinsicElements["table"]) => (
                  <div style={{ overflowX: "auto", maxWidth: "100%" }}>
                    <table>{children}</table>
                  </div>
                ),
                thead: ({ children }: JSX.IntrinsicElements["thead"]) => (
                  <thead>{children}</thead>
                ),
                th: ({ children }: JSX.IntrinsicElements["th"]) => (
                  <th>{children}</th>
                ),
                td: ({ children }: JSX.IntrinsicElements["td"]) => (
                  <td>{children}</td>
                ),
                blockquote: ({ children }: JSX.IntrinsicElements["blockquote"]) => (
                  <blockquote>{children}</blockquote>
                ),
              },
            })
            .processSync(displayMarkdown).result
          );
        } catch (error) {
          return <pre>{displayMarkdown}</pre>;
        }
            }, [displayMarkdown, greyText, darkMode, expandCodeblocks]);
    return <MarkdownViewerWrapper darkMode={darkMode} greyText={greyText} themeColors={themeColors}>
    <div style={{maxWidth: "100%"}}>{content}</div>
    </MarkdownViewerWrapper>;
}


// Copy-to-clipboard helper used by both code blocks and mermaid diagrams
const useCopyToClipboard = () => {
    const [notify, contextHolder] = notification.useNotification();
    const copy = useCallback((text: string) => {
        if (!text) return;
        try {
            navigator.clipboard.writeText(text);
            notify.info({ message: "Copied to clipboard", placement: "bottomLeft", duration: 2 });
        } catch {
            try {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                notify.info({ message: "Copied to clipboard", placement: "bottomLeft", duration: 2 });
            } catch { /* silent */ }
        }
    }, [notify]);
    return { copy, contextHolder };
};

// Language to file extension mapping
const langToExtension: Record<string, string> = {
    sql: '.sql', python: '.py', javascript: '.js', typescript: '.ts',
    java: '.java', json: '.json', html: '.html', css: '.css',
    bash: '.sh', shell: '.sh', yaml: '.yml', xml: '.xml',
    markdown: '.md', r: '.r', go: '.go', rust: '.rs',
    cpp: '.cpp', c: '.c', ruby: '.rb', php: '.php',
};

// Download text content as a file
const downloadTextFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// Download an SVG string rendered to a PNG
const downloadSvgAsPng = (svgHtml: string, filename: string) => {
    const container = document.createElement('div');
    container.innerHTML = svgHtml;
    const svgEl = container.querySelector('svg');
    if (!svgEl) return;

    // Get the natural dimensions from the SVG
    const bbox = { width: svgEl.viewBox?.baseVal?.width || svgEl.width?.baseVal?.value || 800, height: svgEl.viewBox?.baseVal?.height || svgEl.height?.baseVal?.value || 600 };
    const scale = 2; // 2x for retina clarity
    const canvas = document.createElement('canvas');
    canvas.width = bbox.width * scale;
    canvas.height = bbox.height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Serialize SVG with explicit dimensions and inline styles to avoid tainted canvas
    svgEl.setAttribute('width', String(bbox.width));
    svgEl.setAttribute('height', String(bbox.height));
    // Remove any external references that could taint the canvas
    svgEl.querySelectorAll('image').forEach(img => img.remove());
    const svgData = new XMLSerializer().serializeToString(svgEl);
    // Use a data URL instead of an object URL to avoid cross-origin taint
    const url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);

    const img = new Image();
    img.onload = () => {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
            if (!blob) return;
            const pngUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = pngUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(pngUrl);
        }, 'image/png');
    };
    img.src = url;
};

// Shared button style for header actions (copy, download)
const headerActionButtonStyle: React.CSSProperties = {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "0.25em",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

// Download button component
const DownloadButton = ({ onClick, themeColors }: { onClick: (e: React.MouseEvent<HTMLButtonElement>) => void; themeColors: ThemeColors }) => {
    const [hovering, setHovering] = useState(false);
    return (
        <button
            type="button"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onClick={onClick}
            style={headerActionButtonStyle}
            aria-label="Download"
        >
            <svg width={16} height={16} viewBox="0 0 512 512" fill={hovering ? themeColors.textColorSecondary : themeColors.textColorMuted} xmlns="http://www.w3.org/2000/svg">
                <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"/>
            </svg>
        </button>
    );
};

// Copy button component
const CopyButton = ({ onClick, themeColors }: { onClick: (e: React.MouseEvent<HTMLButtonElement>) => void; themeColors: ThemeColors }) => {
    const [hovering, setHovering] = useState(false);
    return (
        <button
            type="button"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            onClick={onClick}
            style={headerActionButtonStyle}
            aria-label="Copy to clipboard"
        >
            <svg width={16} height={16} viewBox="0 0 512 512" fill={hovering ? themeColors.textColorSecondary : themeColors.textColorMuted} xmlns="http://www.w3.org/2000/svg">
                <path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"/>
            </svg>
        </button>
    );
};

// Mermaid diagram renderer component with zoom and pan
const MermaidBlock = ({ code, darkMode = false, expandCodeblocks = true }: { code: string; darkMode?: boolean; expandCodeblocks?: boolean }) => {
    const themeColors = useThemeColors();
    const { copy, contextHolder } = useCopyToClipboard();
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [svg, setSvg] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [mermaidId] = useState<string>(() => `mermaid-diagram-${globalMermaidId++}`);
    const [scale, setScale] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [isOpen, setIsOpen] = useState(expandCodeblocks);
    const dragRef = useRef({ dragging: false, startX: 0, startY: 0, startTx: 0, startTy: 0 });
    const isTransformed = scale !== 1 || translate.x !== 0 || translate.y !== 0;
    const zoomPercent = Math.round(scale * 100);

    useEffect(() => {
        mermaid.initialize({
            startOnLoad: false,
            theme: darkMode ? "dark" : "default",
            securityLevel: "strict",
            flowchart: { useMaxWidth: false, htmlLabels: true, wrappingWidth: 9999 },
            maxTextSize: 100000,
        });
    }, [darkMode]);

    useEffect(() => {
        let cancelled = false;
        const renderDiagram = async () => {
            try {
                const { svg: renderedSvg } = await mermaid.render(mermaidId, code.trim());
                if (!cancelled) {
                    setSvg(renderedSvg);
                    setError(null);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setError(err?.message || "Invalid Mermaid syntax");
                    setSvg("");
                }
                const orphan = document.getElementById(mermaidId);
                if (orphan) orphan.remove();
            }
        };
        renderDiagram();
        return () => { cancelled = true; };
    }, [code, mermaidId, darkMode]);

    // Reset zoom/pan when svg changes
    useEffect(() => {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
    }, [svg]);

    // Attach native wheel listener with passive: false to prevent page scroll
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            // Only zoom when holding Ctrl/Cmd — let normal scrolling pass through
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.03 : 0.03;
            setScale(prev => Math.min(Math.max(0.2, prev + delta), 5));
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [svg, isOpen]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return;
        dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, startTx: translate.x, startTy: translate.y };
    }, [translate]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!dragRef.current.dragging) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        setTranslate({ x: dragRef.current.startTx + dx, y: dragRef.current.startTy + dy });
    }, []);

    const handleMouseUp = useCallback(() => {
        dragRef.current.dragging = false;
    }, []);

    const handleReset = useCallback(() => {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
    }, []);

    const controlBtnStyle: React.CSSProperties = {
        border: "none",
        background: "transparent",
        color: themeColors.textColorMuted,
        cursor: "pointer",
        padding: "0.15em 0.35em",
        lineHeight: 1,
        borderRadius: "3px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    const disabledBtnStyle: React.CSSProperties = {
        ...controlBtnStyle,
        opacity: 0.3,
        cursor: "default",
        pointerEvents: "none",
    };

    if (error) {
        return (
            <>
                {contextHolder}
                <div style={{
                    margin: "0",
                    borderRadius: "0.5em",
                    border: `1px solid ${themeColors.borderColor}`,
                    background: themeColors.backgroundColorSecondary,
                    overflow: "hidden",
                }}>
                    <div
                        onClick={() => setIsOpen(prev => !prev)}
                        style={{
                            padding: "0.5em 1em",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            userSelect: "none",
                            color: themeColors.textColorMuted,
                            fontSize: "0.9em",
                        }}
                    >
                        <span style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                            <span style={{ fontSize: "1.5em", fontWeight: "bold", lineHeight: "1", display: "inline-block", transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>›</span>
                            Diagram error
                        </span>
                        <CopyButton onClick={(e) => { e.stopPropagation(); copy(code); }} themeColors={themeColors} />
                    </div>
                    {isOpen && (
                        <div style={{
                            padding: "0.75em 1em",
                            background: themeColors.backgroundColor,
                            fontSize: "0.85em",
                            color: themeColors.textColorSecondary,
                        }}>
                            <div>{error}</div>
                            <pre style={{ marginTop: "0.5em", whiteSpace: "pre-wrap", color: themeColors.textColorMuted, fontSize: "0.9em" }}>{code}</pre>
                        </div>
                    )}
                </div>
            </>
        );
    }

    if (!svg) {
        return (
            <div style={{
                margin: "0",
                borderRadius: "0.5em",
                border: `1px solid ${themeColors.borderColor}`,
                background: themeColors.backgroundColorSecondary,
                padding: "0.5em 1em",
                fontSize: "0.9em",
                color: themeColors.textColorMuted,
            }}>
                Rendering diagram...
            </div>
        );
    }

    return (
        <>
            {contextHolder}
            <div style={{
                margin: "0",
                borderRadius: "0.5em",
                border: `1px solid ${themeColors.borderColor}`,
                background: themeColors.backgroundColorSecondary,
                overflow: "hidden",
            }}>
                {/* Header — label left, zoom controls center, copy+download right */}
                <div
                    onClick={() => setIsOpen(prev => !prev)}
                    style={{
                        padding: "0.5em 1em",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        userSelect: "none",
                        color: themeColors.textColorMuted,
                        fontSize: "0.9em",
                    }}
                >
                    {/* Left: label + chevron */}
                    <span style={{ display: "flex", alignItems: "center", gap: "0.5em", flex: "1 1 0", minWidth: 0 }}>
                        <span style={{ fontSize: "1.5em", fontWeight: "bold", lineHeight: "1", display: "inline-block", transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>›</span>
                        Diagram
                    </span>
                    {/* Center: zoom controls */}
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "flex", alignItems: "center", gap: "0.1em", flex: "0 0 auto" }}
                    >
                        <button onClick={() => setScale(prev => Math.max(prev - 0.2, 0.2))} style={controlBtnStyle} title="Zoom out">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                        </button>
                        <span style={{ fontSize: "0.75em", color: themeColors.textColorMuted, minWidth: "3em", textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                            {zoomPercent}%
                        </span>
                        <button onClick={() => setScale(prev => Math.min(prev + 0.2, 5))} style={controlBtnStyle} title="Zoom in">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                        </button>
                        <button onClick={isTransformed ? handleReset : undefined} style={isTransformed ? controlBtnStyle : disabledBtnStyle} title="Reset view">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
                        </button>
                    </div>
                    {/* Right: copy + download */}
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flex: "1 1 0", minWidth: 0, gap: "0.25em" }}>
                        <CopyButton onClick={(e) => { e.stopPropagation(); copy(code); }} themeColors={themeColors} />
                        <DownloadButton onClick={(e) => { e.stopPropagation(); downloadSvgAsPng(svg, 'diagram.png'); }} themeColors={themeColors} />
                    </span>
                </div>
                {/* Diagram canvas */}
                {isOpen && (
                    <div
                        ref={containerRef}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        style={{
                            background: themeColors.backgroundColor,
                            cursor: dragRef.current.dragging ? "grabbing" : "grab",
                            overflow: "hidden",
                        }}
                    >
                        <div
                            ref={contentRef}
                            style={{
                                transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                                transformOrigin: "center center",
                                padding: "1em",
                                textAlign: "center",
                                userSelect: "none",
                            }}
                        >
                            <style>{`.mermaid-fit svg { max-width: 100%; height: auto; }`}</style>
                            <div className="mermaid-fit" dangerouslySetInnerHTML={{ __html: svg }} />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

// A more complex custom component for the `code` element.
type CodeBlockProps = JSX.IntrinsicElements["code"] & { greyText?: boolean; darkMode?: boolean; expandCodeblocks?: boolean };

const CodeBlock = ({ children, className, greyText, darkMode = false, expandCodeblocks = true }: CodeBlockProps) => {
    const themeColors = useThemeColors();
    const { copy, contextHolder } = useCopyToClipboard();
    const [codeBlockId] = useState<number>(() => globalCodeBlockId++);
    const [isOpen, setIsOpen] = useState(expandCodeblocks);

    // Highlight.js adds a `className` so this is a hack to detect if the code block
    // is a language block wrapped in a `pre` tag versus an inline `code` tag.
    if (className) {

      // Hide fenced code blocks in grey text mode (status/thinking messages)
      if (greyText) {
        return null;
      }

      // Check if this is a context request JSON block (should be hidden from user)
      // Handle children as string, array, or React elements
      let codeContent = '';
      if (typeof children === 'string') {
        codeContent = children;
      } else if (Array.isArray(children)) {
        codeContent = children.map(child =>
          typeof child === 'string' ? child : (child?.toString() || '')
        ).join('');
      } else if (children) {
        codeContent = children.toString();
      }

      // Detect context request: valid JSON with "context_request": true
      let isContextRequest = false;
      try {
        const trimmedContent = codeContent.trim();
        if (trimmedContent.startsWith('{') && trimmedContent.includes('"context_request"')) {
          const parsed = JSON.parse(trimmedContent);
          if (parsed && typeof parsed === 'object' && parsed.context_request === true) {
            isContextRequest = true;
          }
        }
      } catch (e) {
        isContextRequest = false;
      }

      // Hide context request JSON blocks (the explanation is shown as markdown above it)
      if (isContextRequest) {
        return null;
      }

      // Detect mermaid code blocks and render as diagrams
      const isMermaid = className && /(?:^|\s)language-mermaid(?:\s|$)/.test(className);
      if (isMermaid) {
        return <MermaidBlock code={codeContent} darkMode={darkMode} expandCodeblocks={expandCodeblocks} />;
      }

      // Extract language from className (e.g., "language-sql" -> "sql", "hljs language-javascript" -> "javascript")
      let languageLabel = "Code";
      if (className) {
        const languageMatch = className.match(/(?:^|\s)language-(\w+)/);
        if (languageMatch) {
          languageLabel = languageMatch[1];
        } else {
          const simpleMatch = className.match(/\b(sql|javascript|typescript|python|java|json|html|css|bash|shell|yaml|xml|markdown)\b/i);
          if (simpleMatch) {
            languageLabel = simpleMatch[1].toLowerCase();
          }
        }
      }

      const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          const el = e.currentTarget.closest('details')?.querySelector(`code#md-code-block-${codeBlockId}`) as HTMLElement | null;
          copy(el?.textContent || el?.innerText || '');
      };

      const handleDownload = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          const el = e.currentTarget.closest('details')?.querySelector(`code#md-code-block-${codeBlockId}`) as HTMLElement | null;
          const text = el?.textContent || el?.innerText || '';
          if (!text) return;
          const ext = langToExtension[languageLabel.toLowerCase()] || '.txt';
          downloadTextFile(text, `${languageLabel.toLowerCase()}${ext}`);
      };

      // Handle regular code blocks - collapsible with nice styling
      return (
        <>
          {contextHolder}
          <div style={{
            margin: "0",
            borderRadius: "0.5em",
            border: `1px solid ${themeColors.borderColor}`,
            background: themeColors.backgroundColorSecondary,
            overflow: "hidden"
          }}>
            <div
              onClick={() => setIsOpen(prev => !prev)}
              style={{
                padding: "0.5em 1em",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                userSelect: "none",
                color: themeColors.textColorMuted,
                fontSize: "0.9em",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                <span style={{ fontSize: "1.5em", fontWeight: "bold", lineHeight: "1", display: "inline-block", transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}>›</span>
                {languageLabel}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "0.25em" }}>
                <CopyButton onClick={handleCopy} themeColors={themeColors} />
                <DownloadButton onClick={handleDownload} themeColors={themeColors} />
              </span>
            </div>
            {isOpen && (
              <div style={{
                padding: "0",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "500px",
                background: themeColors.backgroundColor,
              }}>
                <div style={{ overflowX: "auto" }}>
                  <code className={className} id={`md-code-block-${codeBlockId}`}>
                    {children}
                  </code>
                </div>
              </div>
            )}
          </div>
        </>
      );
    }

    // Handle an inline `code` tag.
    return <code>{children}</code>;
  };